import React, { DOMElement, ReactNode } from 'react';
import ReactHtmlParser from 'html-react-parser';

export interface IDetail {
  imgName?: string;
  imgSrc?: string;
  imageSrc?: string;
  title?: Array<{
    type: 'p' | 'a' | 'b' | 'i' | 'br';
    href?: string;
    text: string;
  }>;
  markerPosition?: { x: string; y: string };
  imgStyle?: {
    height: string;
    left: string;
    opacity: string;
    top: string;
    width: string;
  };
  wrapperStatus?: 'info' | 'danger' | 'success';
}

let pageTitle = '';

const isAcceptableImage = (props: {
  src: string;
  alt?: string;
  className?: string;
}) => {
  if (props.src.startsWith('http')) return false;
  if (props.src.startsWith('data:')) return false;
  if (props.alt) return false;
  return !!props.className;
};

const isAcceptableBox = (props: { className?: string }) => {
  if (!props.className) return false;
  if (!props.className.includes('group')) return false;
  return props.className.includes('transition-[margin-bottom]');
};

const isAcceptableTitle = (props: { className?: string }) =>
  props.className &&
  props.className.includes('public-sans') &&
  props.className.includes('text-base');

const isAcceptableWrapper = (props: { className?: string }) =>
  props.className &&
  props.className.includes('h-auto') &&
  props.className.includes('rounded-2xl') &&
  props.className.includes('w-full');

const isMarker = (props: { className?: string }) =>
  props.className &&
  props.className.includes('absolute') &&
  props.className.includes('top-0') &&
  props.className.includes('h-0');

const isPageTitle = (props: { className?: string }) =>
  props.className &&
  props.className.includes('break-words') &&
  props.className.includes('font-bold') &&
  props.className.includes('leading-[30px]');

export function extractBoxes(node: DOMElement<any, any>[]) {
  let boxes: Array<ReactNode> = [];
  React.Children.toArray(node).flatMap((child) => {
    if (!(child && typeof child === 'object' && 'type' in child)) return;

    if (child.type === 'div' && isAcceptableBox(child.props)) {
      boxes.push(child);
    }
    if (
      (child.type === 'span' || child.type === 'h1') &&
      isPageTitle(child.props)
    ) {
      pageTitle = child.props.children as string;
    }
    if (child.props?.children) {
      boxes = [...boxes, ...extractBoxes(child.props.children)];
    }
  });

  return boxes;
}

function extractText(child: React.ReactChild) {
  if (typeof child !== 'object') return [];

  let title: IDetail['title'] = [];
  if (
    typeof child.props.children === 'object' &&
    child.props.children.length > 1
  ) {
    React.Children.toArray(child.props.children).flatMap((itm) => {
      if (itm && typeof itm === 'object' && 'type' in itm) {
        if (itm.type === 'a') {
          title = [
            ...(title as Array<any>),
            {
              type: 'a',
              href: itm.props.href,
              text: itm.props.children[0].props.children,
            },
          ];
        } else if (itm.type === 'strong') {
          title = [
            ...(title as Array<any>),
            {
              type: 'b',
              href: '',
              text: itm.props.children,
            },
          ];
        } else if (itm.type === 'em') {
          title = [
            ...(title as Array<any>),
            {
              type: 'i',
              href: '',
              text: itm.props.children,
            },
          ];
        } else if (itm.type === 'br') {
          title = [
            ...(title as Array<any>),
            {
              type: 'br',
              href: '',
              text: '',
            },
          ];
        }
      } else {
        title = [
          ...(title as Array<any>),
          {
            type: 'p',
            href: '',
            text: itm,
          },
        ];
      }
    });
  } else {
    title = [{ text: child.props.children, type: 'p' }];
  }

  return title;
}

export function extractDetails(box: ReactNode, resultInitialValue: IDetail) {
  let result: IDetail = resultInitialValue;

  if (!box) return false;
  React.Children.toArray(box).flatMap((child) => {
    if (child && typeof child === 'object' && 'type' in child) {
      if (child.type === 'img' && isAcceptableImage(child.props)) {
        const imgNameArray = child.props.src.split('/');
        result.imgName = imgNameArray[imgNameArray.length - 1];
        result.imgStyle = child.props.style;
      }

      if (
        child.type === 'div' &&
        child?.props?.className?.includes('tiptap') &&
        child?.props?.className?.includes('ProseMirror')
      ) {
        React.Children.toArray(child.props.children).flatMap((itm) => {
          if (
            child?.props?.children?.length &&
            child?.props?.children?.length > 1
          ) {
            if (
              typeof itm === 'object' &&
              'type' in itm &&
              itm.type === 'p' &&
              isAcceptableTitle(itm.props)
            ) {
              result.title = [
                ...(result?.title ?? []),
                ...extractText(itm),
                { type: 'br', href: '', text: '' },
              ];
            }
          } else {
            if (
              typeof itm === 'object' &&
              'type' in itm &&
              itm.type === 'p' &&
              isAcceptableTitle(itm.props)
            ) {
              result.title = [...extractText(itm)];
            }
          }
        });
      }

      if (child.type === 'div' && isAcceptableWrapper(child.props)) {
        result.wrapperStatus = child.props.className.includes('green')
          ? 'success'
          : child.props.className.includes('red')
            ? 'danger'
            : 'info';
      }

      if (child.type === 'div' && isMarker(child.props)) {
        result.markerPosition = {
          x: child.props.style.left,
          y: child.props.style.top,
        };
      }

      if (child.props?.children) {
        const tempResult = extractDetails(child.props.children, result);
        if (tempResult) {
          result = tempResult;
        }
      }
    }
  });

  return result;
}

export default function extractImagesAndData(html: string) {
  const parsedHTML = ReactHtmlParser(html) as DOMElement<any, any>[];
  const boxes = extractBoxes(parsedHTML);
  return {
    extractedData: boxes.map((box) => extractDetails(box, {})),
    pageTitle,
  };
}
